import {
	Champion,
	Item,
	MatchSummary,
	MatchTimeline,
	RegionV4,
	RegionV5,
	RuneTree,
	SpectatorGame,
	Summoner,
	SummonerSpell,
} from "../types/riot";

const BASE_URL = import.meta.env.VITE_API_URL;

// ponytail: plain Map beats an LRU dep here — only 2 call sites, cache is advisory
const summaryCache = new Map<string, string>();
const SUMMARY_CACHE_MAX = 100;
// The Riot proxy intermittently drops connections (Safari reports it as the
// opaque "Load failed"), so retry a couple of times and log the exact URL,
// status and body so the real cause is visible in the console/Sentry.
async function getJSON<T>(path: string, init?: RequestInit, attempts = 3): Promise<T> {
	const url = `${BASE_URL}${path}`;
	let lastError: unknown;
	for (let attempt = 1; attempt <= attempts; attempt++) {
		try {
			const res = await fetch(url, init);
			const text = await res.text();
			if (res.status >= 500) {
				throw new Error(`HTTP ${res.status} ${res.statusText}: ${text.slice(0, 500)}`);
			}
			return JSON.parse(text) as T;
		} catch (e) {
			lastError = e;
			console.error(`API ${url} attempt ${attempt}/${attempts} failed:`, e);
			if (attempt < attempts) await new Promise((r) => setTimeout(r, 300 * attempt));
		}
	}
	const detail = lastError instanceof Error ? lastError.message : String(lastError);
	throw new Error(`API GET ${url} failed after ${attempts} attempts: ${detail}`, {
		cause: lastError,
	});
}

interface FetchGameTimelineResponse {
	timeline: MatchTimeline;
}

export async function fetchTimeline(
	region_v5: RegionV5,
	gameId: string,
): Promise<FetchGameTimelineResponse> {
	return getJSON<FetchGameTimelineResponse>(`/timeline/${region_v5}/${gameId}`);
}

interface FetchGameSummaryResponse {
	summary: MatchSummary;
	error?: APIError;
}

export async function fetchGameSummary(
	region_v5: RegionV5,
	gameId: string,
): Promise<FetchGameSummaryResponse> {
	const cacheKey = region_v5 + gameId;
	const cached = summaryCache.get(cacheKey);
	if (cached) return JSON.parse(cached);
	const json = await getJSON<FetchGameSummaryResponse>(`/summary/${region_v5}/${gameId}`);
	cacheSummary(cacheKey, json);
	return json;
}

export async function fetchRSOTimeline(
	token: string,
	region_v5: RegionV5,
	gameId: string,
): Promise<FetchGameTimelineResponse> {
	return getJSON<FetchGameTimelineResponse>(`/rso/timeline/${region_v5}/${gameId}`, {
		headers: { "X-Riot-Token": token },
	});
}

export async function fetchRSOGameSummary(
	token: string,
	region_v5: RegionV5,
	gameId: string,
): Promise<FetchGameSummaryResponse> {
	const cacheKey = region_v5 + gameId;
	const cached = summaryCache.get(cacheKey);
	if (cached) return JSON.parse(cached);
	const json = await getJSON<FetchGameSummaryResponse>(`/rso/summary/${region_v5}/${gameId}`, {
		headers: { "X-Riot-Token": token },
	});
	cacheSummary(cacheKey, json);
	return json;
}

function cacheSummary(key: string, value: FetchGameSummaryResponse) {
	summaryCache.set(key, JSON.stringify(value));
	if (summaryCache.size > SUMMARY_CACHE_MAX) {
		summaryCache.delete(summaryCache.keys().next().value!);
	}
}

export interface APIError {
	message: string;
	code: number;
}

interface FetchSummonerResponse {
	summoner?: Summoner;
	error?: APIError;
}

export async function fetchSummoner(
	region: RegionV5,
	username: string,
): Promise<FetchSummonerResponse> {
	const [name, tag] = username.split("#");
	if (!name || !tag) {
		return { error: { message: "Enter your Riot ID including the tag, e.g. PlayerName#1234", code: 400 } };
	}
	return getJSON<FetchSummonerResponse>(`/summoner/${region}/${name}/${tag}`);
}

interface FetchHistoryResponse {
	history?: string[];
	summoner?: Summoner;
	error?: APIError;
}

// Riot returns an error object instead of an id array on expired token / rate limit.
function normalizeHistory(data: FetchHistoryResponse): FetchHistoryResponse {
	return { ...data, history: Array.isArray(data.history) ? data.history : undefined };
}

export async function fetchHistory(
	region: RegionV5,
	puuid: string,
	queueId: string,
): Promise<FetchHistoryResponse> {
	return normalizeHistory(
		await getJSON<FetchHistoryResponse>(`/history/${region}/puuid/${puuid}/${queueId}`),
	);
}

export async function fetchRSOHistory(
	token: string,
	region: RegionV5,
	queueId: string,
): Promise<FetchHistoryResponse> {
	return normalizeHistory(
		await getJSON<FetchHistoryResponse>(`/rso/history/${region}`, {
			headers: { "X-Riot-Token": token },
		}),
	);
}

interface FetchStaticsResponse {
	champions: Record<string, Champion>;
	summoners: Record<string, SummonerSpell>;
	items: Record<string, Item>;
	runes: Array<RuneTree>;
	version: string;
}

export async function fetchStatics(): Promise<FetchStaticsResponse> {
	return getJSON<FetchStaticsResponse>("/dragon");
}

interface FetchSpectatorResponse {
	spectator?: SpectatorGame;
	error?: APIError;
}

export async function fetchSpectator(
	region: RegionV4,
	summonerId: string,
): Promise<FetchSpectatorResponse> {
	return getJSON<FetchSpectatorResponse>(`/spectator/${region}/${summonerId}`);
}

export interface FeaturedGameParticipant {
	riotId: string;
	puuid: string;
}

interface FetchFeaturedGamesResponse {
	spectator?: {
		gameList: {
			gameQueueConfigId: number;
			participants: FeaturedGameParticipant[];
		}[];
	};
}

export async function fetchFeaturedGames(region: RegionV4): Promise<FetchFeaturedGamesResponse> {
	return getJSON<FetchFeaturedGamesResponse>(`/featured/${region}`);
}

export async function fetchLatestGameVersion(): Promise<{ version: string }> {
	return getJSON<{ version: string }>("/version");
}
